import React from 'react';
import { C, CARD_W, VOTE_CARD_H, imgUrl, audioUrl, normalizeImgField, normalizeAudioField } from '../flowCanvas.constants';
import AudioWaveformCell from './AudioWaveformCell';

// ─── Vote card node — one card per handObject graphic in the wave stack ────────
export default function VoteCardNode({
  graphicItem, graphicIndex, teamLabel, selected, onClick,
  overrideX, overrideY, assetFolder, campaignId, onDelete, teamTotalFreq,
  onTogglePlaySFX, onToggleDefaultSFX, onCustomAudioClick,
  cardWidth = CARD_W,
  burstAnimLabel,   // optional — when set, replaces SFX section with burst anim info
}) {
  const W = cardWidth, H = VOTE_CARD_H;
  const x = overrideX, y = overrideY;
  const left = x - W / 2, top = y - H / 2;

  const imgNorm = normalizeImgField(graphicItem.filename);
  const imgSrc  = imgNorm.srcUrl
    ? imgNorm.srcUrl
    : imgNorm.name ? imgUrl(imgNorm.name, imgNorm.isDefault, assetFolder, campaignId) : null;

  const playSFX     = graphicItem.playSFX === true;
  const overrideSFX = playSFX && graphicItem.overrideSFX === true;

  const freq      = graphicItem.appearFrequency ?? 100;
  const totalFreq = teamTotalFreq > 0 ? teamTotalFreq : freq;
  const freqPct   = Math.round(freq / totalFreq * 100);

  const audNorm  = normalizeAudioField(graphicItem.audioFilename);
  const audioSrc = audNorm.srcUrl
    ? audNorm.srcUrl
    : audNorm.name ? audioUrl(audNorm.name, audNorm.isDefault, assetFolder, campaignId) : null;

  const hasImg = !!imgSrc;
  const accent = hasImg ? C.purple : imgNorm.name ? C.orange : C.purpleDim;

  // Layout positions relative to top = y − H/2
  const IMG_Y   = top + 36;
  const IMG_H   = 100;
  const IMG_W   = W - 12;
  const FNAME_Y = IMG_Y + IMG_H + 12;
  const DIV_Y   = FNAME_Y + 9;
  // Give the audio waveform a little more breathing room from the bottom.
  const SFX_Y   = DIV_Y - 2;

  const imgName = imgNorm.name
    ? (imgNorm.name.length > 26 ? imgNorm.name.slice(0, 26) + '…' : imgNorm.name)
    : 'No image set';
  const volPct = typeof graphicItem.volume === 'number' ? graphicItem.volume : undefined;

  const clipId    = `fc-imgclip-${(overrideX | 0)}-${(overrideY | 0)}`;
  const checkerId = `fc-checker-${(overrideX | 0)}-${(overrideY | 0)}`;

  return (
    <g className="fc-node fc-node--vote-card" onClick={onClick} style={{ cursor: 'pointer' }}>
      <defs>
        <clipPath id={clipId}>
          <rect x={left + 6} y={IMG_Y} width={IMG_W} height={IMG_H} rx="4" />
        </clipPath>
        <pattern id={checkerId} patternUnits="userSpaceOnUse" width="8" height="8">
          <rect width="8" height="8" fill="#555" />
          <rect width="4" height="4" fill="#333" />
          <rect x="4" y="4" width="4" height="4" fill="#333" />
        </pattern>
      </defs>

      {/* Shadow */}
      <rect x={left + 2} y={top + 4} width={W} height={H} rx="8" fill="#000" fillOpacity="0.3" />
      {/* Card body */}
      <rect x={left} y={top} width={W} height={H} rx="8"
        fill={selected ? C.cardSel : C.card}
        stroke={selected ? C.sel : accent}
        strokeWidth={selected ? 2 : 1.5}
      />

      {/* Delete button */}
      {onDelete && (
        <g onClick={e => { e.stopPropagation(); onDelete(); }} style={{ cursor: 'pointer' }}>
          <circle cx={left + W} cy={top} r={11} fill="#dc2626" />
          <g transform={`translate(${left + W}, ${top})`} style={{ pointerEvents: 'none', userSelect: 'none' }}>
            <rect x="-2.5" y="-6" width="5" height="2.5" rx="1.2" fill="#fee2e2" />
            <rect x="-6" y="-4" width="12" height="2" rx="1" fill="#fee2e2" />
            <rect x="-4.5" y="-1.5" width="9" height="8" rx="1" fill="#fee2e2" />
            <rect x="-2" y="0" width="1.2" height="5" rx="0.6" fill="#dc2626" />
            <rect x="0.8" y="0" width="1.2" height="5" rx="0.6" fill="#dc2626" />
          </g>
        </g>
      )}

      {/* Header */}
      <text x={left + 10} y={top + 17} fontSize="9.5" fill={C.textMuted}
        style={{ pointerEvents: 'none', userSelect: 'none' }}>{teamLabel}</text>
      <text x={left + W - 10} y={top + 17} textAnchor="end" fontSize="9.5" fontWeight="700" fill={C.text}
        style={{ pointerEvents: 'none', userSelect: 'none' }}>Vote #{graphicIndex + 1}</text>

      {/* Image area — chequered background */}
      <rect x={left + 6} y={IMG_Y} width={IMG_W} height={IMG_H} rx="4"
        fill={`url(#${checkerId})`} clipPath={`url(#${clipId})`} />

      {imgSrc ? (
        <image href={imgSrc}
          x={left + 6} y={IMG_Y} width={IMG_W} height={IMG_H}
          preserveAspectRatio="xMidYMid meet"
          clipPath={`url(#${clipId})`}
          style={{ pointerEvents: 'none' }} />
      ) : (
        <>
          <rect x={left + 6} y={IMG_Y} width={IMG_W} height={IMG_H} rx="4"
            fill="none" stroke={C.purpleDim} strokeWidth="1.5" strokeDasharray="4 3" />
          <text x={left + W / 2} y={IMG_Y + IMG_H / 2 - 6}
            textAnchor="middle" dominantBaseline="middle" fontSize="28" fill={C.textDim}
            style={{ pointerEvents: 'none', userSelect: 'none' }}>⊕</text>
          <text x={left + W / 2} y={IMG_Y + IMG_H / 2 + 20}
            textAnchor="middle" fontSize="8.25" fill={C.textDim}
            style={{ pointerEvents: 'none', userSelect: 'none' }}>Click to set image</text>
        </>
      )}

      {/* Image border overlay */}
      <rect x={left + 6} y={IMG_Y} width={IMG_W} height={IMG_H} rx="4"
        fill="none" stroke={accent} strokeWidth="0.75" strokeOpacity="0.35" />

      {/* Frequency % badge */}
      <g style={{ pointerEvents: 'none', userSelect: 'none' }}>
        <rect x={x - 20} y={IMG_Y + IMG_H - 15} width={40} height={13} rx="6"
          fill="#000" fillOpacity="0.65" clipPath={`url(#${clipId})`} />
        <text x={x} y={IMG_Y + IMG_H - 8}
          textAnchor="middle" dominantBaseline="middle"
          fontSize="8" fontWeight="700" fill="#86efac">
          {freqPct}%
        </text>
      </g>

      {/* Filename */}
      <text x={left + W / 2} y={FNAME_Y} textAnchor="middle" fontSize="8.25" fill={C.textMuted}
        style={{ pointerEvents: 'none', userSelect: 'none' }}>{imgName}</text>

      {/* Divider */}
      <line x1={left + 10} y1={DIV_Y} x2={left + W - 10} y2={DIV_Y} stroke={C.border} strokeWidth="0.75" />

      {/* Bottom section — burst anim label (object zapper) OR SFX (wave) */}
      {burstAnimLabel ? (
        <text x={left + W / 2} y={SFX_Y + 20} textAnchor="middle" fontSize="10.5" fontWeight="600" fill={C.purple}
          style={{ pointerEvents: 'none', userSelect: 'none' }}>{burstAnimLabel}</text>
      ) : (
      <>
      <g onClick={(event) => { event.stopPropagation(); onTogglePlaySFX?.(); }} style={{ cursor: 'pointer' }}>
        <rect x={left + 10} y={SFX_Y + 4} width={84} height={22} rx={11}
          fill={playSFX ? 'rgba(57,211,132,0.13)' : C.circle}
          stroke={playSFX ? C.greenText : C.border} strokeWidth="1" />
        <circle cx={left + 21} cy={SFX_Y + 15} r="5" fill={playSFX ? C.greenText : C.textDim} />
        <text x={left + 31} y={SFX_Y + 19} fontSize="9" fontWeight="700" fill={playSFX ? C.greenText : C.textMuted}
          style={{ pointerEvents: 'none', userSelect: 'none' }}>
          SFX {playSFX ? 'On' : 'Off'}
        </text>
      </g>
      {playSFX && (
        <g onClick={(event) => { event.stopPropagation(); onToggleDefaultSFX?.(); }} style={{ cursor: 'pointer' }}>
          <rect x={left + 102} y={SFX_Y + 4} width={116} height={22} rx={11}
            fill={!overrideSFX ? 'rgba(145,70,255,0.18)' : C.circle}
            stroke={!overrideSFX ? C.purple : C.border} strokeWidth="1" />
          <circle cx={left + 113} cy={SFX_Y + 15} r="5" fill={!overrideSFX ? C.greenText : C.textDim} />
          <text x={left + 123} y={SFX_Y + 19} fontSize="9" fontWeight="700" fill={!overrideSFX ? C.purple : C.textMuted}
            style={{ pointerEvents: 'none', userSelect: 'none' }}>
            Default {!overrideSFX ? 'On' : 'Off'}
          </text>
        </g>
      )}
      {overrideSFX && (
        <>
          <text x={left + 12} y={SFX_Y + 44} fontSize="10" fontWeight="700" fill={C.greenText}
            style={{ pointerEvents: 'none', userSelect: 'none' }}>SFX</text>
          <g onClick={(event) => { event.stopPropagation(); onCustomAudioClick?.(); }} style={{ cursor: 'pointer' }}>
            <rect x={left + 10} y={SFX_Y + 52} width={W - 20} height={78} rx="8"
              fill="rgba(57,211,132,0.08)" stroke={C.greenText} strokeOpacity="0.7" strokeWidth="1.5" />
            <text x={left + 24} y={SFX_Y + 72} fontSize="12" fill={C.greenText} style={{ pointerEvents: 'none', userSelect: 'none' }}>♪</text>
            <text x={left + 43} y={SFX_Y + 72} fontSize="10" fontWeight="700" fill={C.text}
              style={{ pointerEvents: 'none', userSelect: 'none' }}>
              {audNorm.name || 'Choose custom audio'}
            </text>
            <rect x={left + W - 67} y={SFX_Y + 104} width={45} height={17} rx="5" fill="rgba(255,255,255,0.07)" />
            <text x={left + W - 44} y={SFX_Y + 116} textAnchor="middle" fontSize="8" fill={C.textMuted}
              style={{ pointerEvents: 'none', userSelect: 'none' }}>VOL {Math.round(volPct ?? 100)}%</text>
          </g>
          <foreignObject x={left + 20} y={SFX_Y + 78} width={W - 40} height="26">
            <div xmlns="http://www.w3.org/1999/xhtml" style={{ width: '100%', height: '100%' }}>
              <AudioWaveformCell srcUrl={audioSrc} height={26} onSelect={onCustomAudioClick} />
            </div>
          </foreignObject>
          {!audNorm.name && <text x={left + W - 20} y={SFX_Y + 94} textAnchor="end" fontSize="8" fill={C.textDim}
            style={{ pointerEvents: 'none', userSelect: 'none' }}>Click to select</text>}
        </>
      )}
      </>
      )}
    </g>
  );
}
